import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import HelpOutlineTwoToneIcon from "@mui/icons-material/HelpOutlineTwoTone";
import { BarChart } from "@mui/x-charts/BarChart";
import FiltroAtendimentos from "../Filtros/FiltroAtendimentos";
import { getAtendimentosCliente } from "../Endpoints/AssistentePreVendaEndPoints";

const DEFAULT_FILTER = "Mes atual";
const SPECIFIC_DATES_FILTER = "Datas Especificas";
const FILTER_RANGES = {
  "Ultimos 7 dias": { days: 7 },
  "Ultimos 30 dias": { days: 30 },
  "Ultimos 3 meses": { months: 3 },
  "Ultimos 6 meses": { months: 6 },
  "Ultimos 12 meses": { months: 12 },
};
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfPreviousMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 0);
const subtractMonthsKeepingDay = (date, months) => {
  const totalMonths = date.getFullYear() * 12 + date.getMonth() - months;
  let targetYear = Math.floor(totalMonths / 12);
  let targetMonth = totalMonths % 12;
  if (targetMonth < 0) {
    targetMonth += 12;
    targetYear -= 1;
  }
  const targetDay = Math.min(date.getDate(), daysInMonth(targetYear, targetMonth));
  return new Date(targetYear, targetMonth, targetDay);
};
const toInputKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
const parseInputDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const startOfWeekFromRange = (date, rangeStart) => {
  const normalized = startOfDay(date);
  const rangeAnchor = startOfDay(rangeStart);
  const diffDays = Math.floor((normalized - rangeAnchor) / MS_PER_DAY);
  const offset = Math.floor(diffDays / 7) * 7;
  const bucketStart = new Date(rangeAnchor);
  bucketStart.setDate(rangeAnchor.getDate() + offset);
  return bucketStart;
};

const formatLocalDate = (date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}/${date.getFullYear()}`;
const formatWeekRangeLabel = (weekStart, endDate) => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const cappedEnd = weekEnd > endDate ? endDate : weekEnd;
  return `${formatLocalDate(weekStart)} - ${formatLocalDate(cappedEnd)}`;
};

const resolveAtendimentoDate = (item) =>
  item?.dataHoraCadastro || item?.dataHoraCadastroAtualizado;

const getDataRange = (atendimentos) => {
  const dates = atendimentos
    .map((item) => new Date(resolveAtendimentoDate(item)))
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => a - b);

  if (!dates.length) return null;

  return {
    minDate: startOfDay(dates[0]),
    maxDate: startOfDay(dates[dates.length - 1]),
  };
};

const resolveRangeAndGrouping = (
  filtro,
  atendimentos,
  selectedYear,
  startDateInput,
  endDateInput,
  viewMode
) => {
  const today = startOfDay(new Date());
  let startDate = null;
  let endDate = today;
  let totalDays = 0;

  if (filtro === SPECIFIC_DATES_FILTER) {
    const parsedStartRaw = startDateInput ? parseInputDate(startDateInput) : null;
    const parsedEndRaw = endDateInput ? parseInputDate(endDateInput) : null;
    const parsedStart = parsedStartRaw ? startOfDay(parsedStartRaw) : null;
    const parsedEnd = parsedEndRaw ? startOfDay(parsedEndRaw) : null;

    if (parsedStart || parsedEnd) {
      const dataRange = getDataRange(atendimentos);

      if (parsedStart && parsedEnd) {
        startDate = parsedStart;
        endDate = parsedEnd;
      } else if (parsedStart) {
        startDate = parsedStart;
        endDate = dataRange?.maxDate || today;
      } else {
        startDate = dataRange?.minDate || parsedEnd;
        endDate = parsedEnd;
      }
      totalDays = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
    }
  } else if (filtro === "Mes atual") {
    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    totalDays = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
  } else if (filtro === "Anual") {
    const parsedYear = Number(selectedYear);
    const resolvedYear = Number.isNaN(parsedYear) ? today.getFullYear() : parsedYear;
    startDate = new Date(resolvedYear, 0, 1);
    endDate =
      resolvedYear === today.getFullYear()
        ? today
        : startOfDay(new Date(resolvedYear, 11, 31));
    totalDays = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
  } else if (FILTER_RANGES[filtro]?.days) {
    const rangeEnd = new Date(today);
    rangeEnd.setDate(today.getDate() - 1);
    endDate = startOfDay(rangeEnd);
    startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - FILTER_RANGES[filtro].days + 1);
    totalDays = FILTER_RANGES[filtro].days;
  } else if (FILTER_RANGES[filtro]?.months) {
    const currentMonthStart = startOfMonth(today);
    endDate = endOfPreviousMonth(currentMonthStart);
    startDate = startOfDay(subtractMonthsKeepingDay(currentMonthStart, FILTER_RANGES[filtro].months));
    totalDays = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
  }

  if (!startDate) {
    const dataRange = getDataRange(atendimentos);
    if (!dataRange) {
      return { startDate: today, endDate: today, groupBy: "day" };
    }
    startDate = dataRange.minDate;
    endDate = dataRange.maxDate;
    totalDays = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
  }

  let groupBy = "day";
  if (viewMode === "Mes") {
    groupBy = "month";
  } else if (viewMode === "Ano") {
    groupBy = "year";
  } else if (viewMode !== "Dia") {
    if (totalDays > 60) {
      groupBy = "month";
    } else if (totalDays > 10) {
      groupBy = "week";
    }
  }

  return { startDate, endDate, groupBy };
};

const aggregateAtendimentosParaGrafico = (
  atendimentos,
  filtro,
  selectedYear,
  startDateInput,
  endDateInput,
  viewMode
) => {
  if (!Array.isArray(atendimentos) || atendimentos.length === 0) {
    return { labels: [], data: [] };
  }

  const { startDate, endDate, groupBy } = resolveRangeAndGrouping(
    filtro,
    atendimentos,
    selectedYear,
    startDateInput,
    endDateInput,
    viewMode
  );
  const buckets = new Map();

  atendimentos.forEach((item) => {
    const rawDate = resolveAtendimentoDate(item);
    if (!rawDate) return;
    const date = new Date(rawDate);
    if (isNaN(date.getTime())) return;

    const day = startOfDay(date);
    if (day < startDate || day > endDate) return;

    let key = "";
    if (groupBy === "week") {
      key = toInputKey(startOfWeekFromRange(day, startDate));
    } else if (groupBy === "month") {
      key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}`;
    } else if (groupBy === "year") {
      key = `${day.getFullYear()}`;
    } else {
      key = toInputKey(day);
    }

    buckets.set(key, (buckets.get(key) || 0) + 1);
  });

  const labels = [];
  const data = [];

  if (groupBy === "week") {
    let current = startOfWeekFromRange(startDate, startDate);
    const endWeek = startOfWeekFromRange(endDate, startDate);
    while (current <= endWeek) {
      const key = toInputKey(current);
      labels.push(formatWeekRangeLabel(current, endDate));
      data.push(buckets.get(key) || 0);
      current.setDate(current.getDate() + 7);
    }
    return { labels, data };
  }

  if (groupBy === "month") {
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    while (current <= endMonth) {
      const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
      labels.push(
        current.toLocaleDateString("pt-BR", { month: "long" })
      );
      data.push(buckets.get(key) || 0);
      current.setMonth(current.getMonth() + 1);
    }
    return { labels, data };
  }

  if (groupBy === "year") {
    let currentYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    while (currentYear <= endYear) {
      const key = `${currentYear}`;
      labels.push(key);
      data.push(buckets.get(key) || 0);
      currentYear += 1;
    }
    return { labels, data };
  }

  let current = new Date(startDate);
  while (current <= endDate) {
    const key = toInputKey(current);
    labels.push(formatLocalDate(current));
    data.push(buckets.get(key) || 0);
    current.setDate(current.getDate() + 1);
  }

  return { labels, data };
};

const buildAtendimentosParams = (
  filtro,
  selectedYear,
  startDateInput,
  endDateInput
) => {
  if (filtro === SPECIFIC_DATES_FILTER) {
    return {
      ...(startDateInput ? { dataInicio: startDateInput } : {}),
      ...(endDateInput ? { dataFim: endDateInput } : {}),
    };
  }

  if (filtro === "Anual") {
    const parsedYear = Number(selectedYear);
    const resolvedYear = Number.isNaN(parsedYear)
      ? new Date().getFullYear()
      : parsedYear;
    const startDate = new Date(resolvedYear, 0, 1);
    const endDate =
      resolvedYear === new Date().getFullYear()
        ? new Date()
        : new Date(resolvedYear, 11, 31);
    return {
      dataInicio: toInputKey(startDate),
      dataFim: toInputKey(endDate),
    };
  }

  if (filtro === "Mes atual") {
    const today = startOfDay(new Date());
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      dataInicio: toInputKey(startDate),
      dataFim: toInputKey(today),
    };
  }

  if (FILTER_RANGES[filtro]?.days) {
    const today = startOfDay(new Date());
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 1);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - FILTER_RANGES[filtro].days + 1);
    return {
      dataInicio: toInputKey(startDate),
      dataFim: toInputKey(endDate),
    };
  }

  if (FILTER_RANGES[filtro]?.months) {
    const today = startOfDay(new Date());
    const currentMonthStart = startOfMonth(today);
    const endDate = endOfPreviousMonth(currentMonthStart);
    const startDate = startOfDay(
      subtractMonthsKeepingDay(currentMonthStart, FILTER_RANGES[filtro].months)
    );
    return {
      dataInicio: toInputKey(startDate),
      dataFim: toInputKey(endDate),
    };
  }

  return {};
};

const GraficoVolumeAtendimentos = ({ tooltipText = "" }) => {
  const theme = useTheme();
  const axisTextColor = theme.palette.text.secondary;
  const [localDateFilter, setLocalDateFilter] = useState(DEFAULT_FILTER);
  const [appliedDateFilter, setAppliedDateFilter] = useState(DEFAULT_FILTER);
  const [selectedYear, setSelectedYear] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [viewMode, setViewMode] = useState("Dia");
  const [atendimentos, setAtendimentos] = useState([]);
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(false);
  const [, setAtendimentosError] = useState(null);
  const clearFilters = () => {
    setLocalDateFilter(DEFAULT_FILTER);
    setAppliedDateFilter(DEFAULT_FILTER);
    setSelectedYear("");
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setViewMode("Dia");
  };

  useEffect(() => {
    let isActive = true;

    const fetchAtendimentos = async () => {
      setLoadingAtendimentos(true);
      setAtendimentosError(null);
      try {
        const params = buildAtendimentosParams(
          appliedDateFilter,
          selectedYear,
          appliedStartDate,
          appliedEndDate
        );
        const response = await getAtendimentosCliente(params);
        if (!isActive) return;
        setAtendimentos(Array.isArray(response) ? response : []);
      } catch (error) {
        if (!isActive) return;
        setAtendimentos([]);
        setAtendimentosError(error);
      } finally {
        if (isActive) {
          setLoadingAtendimentos(false);
        }
      }
    };

    fetchAtendimentos();
    return () => {
      isActive = false;
    };
  }, [appliedDateFilter, selectedYear, appliedStartDate, appliedEndDate]);

  const { labels, data } = useMemo(() => {
    return aggregateAtendimentosParaGrafico(
      atendimentos,
      appliedDateFilter,
      selectedYear,
      appliedStartDate,
      appliedEndDate,
      viewMode
    );
  }, [
    atendimentos,
    appliedDateFilter,
    selectedYear,
    appliedStartDate,
    appliedEndDate,
    viewMode,
  ]);

  const totalAtendimentos = data.reduce((acc, value) => acc + value, 0);
  const hasData = data.length > 0 && totalAtendimentos > 0;
  const isScrollable = data.length > 13;

  const formatAxisLabel = (label, context) => {
    if (!label) return label;
    if (context?.location === "tooltip") {
      return label;
    }
    if (viewMode === "Dia" || viewMode === "Mes" || viewMode === "Ano") {
      return label;
    }
    if (typeof label === "string" && label.includes(" - ")) {
      const [start, end] = label.split(" - ");
      const shortStart = start?.slice(0, 5);
      const shortEnd = end?.slice(0, 5);
      if (shortStart && shortEnd) {
        return `${shortStart} - ${shortEnd}`;
      }
    }
    return label.length > 10 ? `${label.slice(0, 10)}...` : label;
  };

  return (
    <Box
      sx={{
        height: "100%",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        boxShadow: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        minHeight: 400,
      }}
    >
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontSize: { xs: "1.125rem", md: "1.25rem" },
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          Atendimentos
          {tooltipText?.trim() && (
            <Tooltip
              title={tooltipText}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: { fontSize: "0.95rem", lineHeight: 1.4, p: 1.25 },
                },
              }}
            >
              <IconButton
                size="medium"
                aria-label="Ajuda do grafico de atendimentos"
                sx={{ p: 0.75 }}
              >
                <HelpOutlineTwoToneIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
        <Box sx={{ display: "flex" }}>
          <FiltroAtendimentos
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            dateFilter={localDateFilter}
            selectedYear={selectedYear}
            startDate={startDate}
            endDate={endDate}
            onChange={(value) => {
              setLocalDateFilter(value);

              if (value === "Anual") {
                setSelectedYear("");
                setAppliedStartDate("");
                setAppliedEndDate("");
                setStartDate("");
                setEndDate("");
                return;
              }

              if (value === SPECIFIC_DATES_FILTER) {
                const today = new Date();
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                setStartDate(toInputKey(firstDayOfMonth));
                setEndDate("");
                return;
              }

              setAppliedDateFilter(value);
              setAppliedStartDate("");
              setAppliedEndDate("");
              setStartDate("");
              setEndDate("");
            }}
            onYearChange={(value) => {
              setSelectedYear(value);
              if (localDateFilter === "Anual" && value) {
                setAppliedDateFilter("Anual");
              }
            }}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApplyFilters={() => {
              setAppliedDateFilter(SPECIFIC_DATES_FILTER);
              setAppliedStartDate(startDate);
              setAppliedEndDate(endDate);
            }}
            onClearFilters={clearFilters}
          />
        </Box>

      </Box>

      {loadingAtendimentos ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)",
            borderRadius: 2,
            position: "relative",
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
          aria-busy="true"
        >
          <CircularProgress size={32} />
        </Box>
      ) : hasData ? (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 200, md: 280 },
              display: "flex",
              justifyContent: isScrollable ? "flex-start" : "center",
              alignItems: "center",
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, rgba(118, 75, 162, 0.06) 100%)",
              borderRadius: 2,
              position: "relative",
              overflowX: isScrollable ? "auto" : "hidden",
            }}
            aria-busy="false"
            aria-label="Grafico de barras com volume de atendimentos"
          >
            <Box
              sx={{
                minWidth: isScrollable ? data.length * 70 : "100%",
                px: 2,
              }}
            >
              <BarChart
                series={[
                  {
                    data,
                    color: "#667eea",
                    showLabel: false,
                  },
                ]}
                xAxis={[
                  {
                    data: labels,
                    scaleType: "band",
                    valueFormatter: formatAxisLabel,
                    tickLabelStyle: {
                      fontSize: 11,
                      angle: -45,
                      textAnchor: "end",
                      fill: axisTextColor,
                    },
                  },
                ]}
                yAxis={[
                  {
                    tickLabelStyle: {
                      fontSize: 11,
                      fill: axisTextColor,
                    },
                  },
                ]}
                barLabel={(item, context) =>
                  context.bar.height < 12 ? null : item.value?.toString()
                }
                grid={{ horizontal: false, vertical: false }}
                height={260}
                margin={{ top: 20, right: 20, bottom: 70, left: 50 }}
                borderRadius={10}
                components={{
                  Bar: (props) => <rect {...props} rx={15} ry={15} />,
                }}
                sx={{
                  "& .MuiBarElement-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: "1rem",
                }}
              >
                Total Atendimentos:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#667eea",
                  fontSize: "1rem",
                }}
              >
                {totalAtendimentos.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)",
            borderRadius: 2,
            position: "relative",
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
          aria-busy="false"
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.875rem", md: "1rem" },
              maxWidth: 300,
              lineHeight: 1.5,
            }}
          >
            Nenhum atendimento encontrado no periodo selecionado.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GraficoVolumeAtendimentos;
